import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { FormField, FormLabel, FormInput, FormSelect, Button } from '../ui';
import type { CampaignLeadFilters } from '../../types/campaignLead';
import { campaignService } from '../../services/campaign.service';
import { leadService } from '../../services/lead.service';
import { campaignLeadService } from '../../services/campaignLead.service';

interface CampaignLeadFiltersProps {
  filters: CampaignLeadFilters;
  onFilterChange: (filters: CampaignLeadFilters) => void;
  onClearFilters: () => void;
}

function CampaignLeadFiltersComponent({
  filters,
  onFilterChange,
  onClearFilters,
}: CampaignLeadFiltersProps) {
  const [localFilters, setLocalFilters] = useState<CampaignLeadFilters>(filters);

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns-filter'],
    queryFn: () => campaignService.getAll(1, 1000),
  });

  const { data: leads } = useQuery({
    queryKey: ['leads-filter'],
    queryFn: () => leadService.getAll(1, 1000),
  });

  const { data: options } = useQuery({
    queryKey: ['campaign-lead-options'],
    queryFn: () => campaignLeadService.getOptions(),
  });

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({
      ...prev,
      [name]: value || undefined,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFilterChange(localFilters);
  };

  const handleClear = () => {
    setLocalFilters({});
    onClearFilters();
  };

  const scoreLegend = [
    { range: '80-100', label: 'Alta Qualidade', color: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700' },
    { range: '60-79', label: 'Boa Qualidade', color: 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' },
    { range: '40-59', label: 'Média Qualidade', color: 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700' },
    { range: '20-39', label: 'Baixa Qualidade', color: 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700' },
    { range: '0-19', label: 'Qualidade Muito Baixa', color: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700' },
  ];

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <FormField>
          <FormLabel htmlFor="search">Buscar</FormLabel>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <FormInput
              id="search"
              name="search"
              type="text"
              value={localFilters.search || ''}
              onChange={handleChange}
              placeholder="Nome do lead, campanha..."
              className="pl-10"
            />
          </div>
        </FormField>

        <FormField>
          <FormLabel htmlFor="campaign_id">Campanha</FormLabel>
          <FormSelect
            id="campaign_id"
            name="campaign_id"
            value={localFilters.campaign_id || ''}
            onChange={handleChange}
          >
            <option value="">Todas</option>
            {campaigns?.data?.map(campaign => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="stage">Estágio</FormLabel>
          <FormSelect
            id="stage"
            name="stage"
            value={localFilters.stage || ''}
            onChange={handleChange}
          >
            <option value="">Todos</option>
            {options?.stages?.map(stage => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="action">Ação</FormLabel>
          <FormSelect
            id="action"
            name="action"
            value={localFilters.action || ''}
            onChange={handleChange}
          >
            <option value="">Todas</option>
            {options?.actions?.map(action => (
              <option key={action} value={action}>
                {action}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="type">Tipo</FormLabel>
          <FormSelect id="type" name="type" value={localFilters.type || ''} onChange={handleChange}>
            <option value="">Todos</option>
            {options?.types?.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="score_min">Score Mínimo</FormLabel>
          <FormInput
            id="score_min"
            name="score_min"
            type="number"
            min="0"
            max="32767"
            value={localFilters.score_min || ''}
            onChange={handleChange}
            placeholder="0"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="score_max">Score Máximo</FormLabel>
          <FormInput
            id="score_max"
            name="score_max"
            type="number"
            min="0"
            max="100"
            value={localFilters.score_max || ''}
            onChange={handleChange}
            placeholder="100"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="ticket_id">ID do Ticket</FormLabel>
          <FormInput
            id="ticket_id"
            name="ticket_id"
            type="number"
            value={localFilters.ticket_id || ''}
            onChange={handleChange}
            placeholder="ID do ticket"
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="archived">Status</FormLabel>
          <FormSelect
            id="archived"
            name="archived"
            value={localFilters.archived || ''}
            onChange={handleChange}
          >
            <option value="">Todos</option>
            <option value="false">Ativos</option>
            <option value="true">Arquivados</option>
          </FormSelect>
        </FormField>
      </div>

      <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Legenda de Score
        </h3>
        <div className="flex flex-wrap gap-2">
          {scoreLegend.map((item) => (
            <div
              key={item.range}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs ${item.color}`}
            >
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {item.range}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit">
          <Search className="h-4 w-4 mr-2" />
          Filtrar
        </Button>
        <Button type="button" variant="secondary" onClick={handleClear}>
          <X className="h-4 w-4 mr-2" />
          Limpar
        </Button>
      </div>
    </form>
  );
}

export default CampaignLeadFiltersComponent;
